import {
  Ship,
  Wrench,
  History,
  BellRing,
  Fuel,
  ScanLine,
  FileText,
  Share2,
} from "lucide-react";
import Reveal from "@/components/Reveal";

const FEATURES = [
  {
    id: "boat-records",
    icon: Ship,
    title: "Boat & engine records",
    body: "Specs, engines, serial numbers, and a full configuration profile for every boat you own — switch between them in a tap.",
  },
  {
    id: "smart-maintenance",
    icon: Wrench,
    title: "Smart maintenance engine",
    body: "A rules engine matches manufacturer-style service intervals to your boat's exact setup, tracked per engine.",
  },
  {
    id: "service-history",
    icon: History,
    title: "Service history & repairs",
    body: "Log maintenance and repairs with dates, engine hours, vendors, and costs. Upcoming, overdue, and completed — always sorted.",
  },
  {
    id: "reminders",
    icon: BellRing,
    title: "Reminders & expirations",
    body: "On-device reminders before service is due, plus a heads-up before registrations, warranties, and documents expire.",
  },
  {
    id: "trips-fuel",
    icon: Fuel,
    title: "Trip & fuel log",
    body: "Log trips with engine hours, fuel, and cost — with local weather auto-filled and efficiency charts over time.",
  },
  {
    id: "receipt-scan",
    icon: ScanLine,
    title: "AI receipt scanner",
    body: "Snap a receipt and let AI extract the vendor, date, totals, and line items — filed automatically with the photo attached.",
  },
  {
    id: "history-report",
    icon: FileText,
    title: "Boat history report",
    body: "Turn years of records into a polished, premium history report for mechanics, surveyors, or resale.",
  },
  {
    id: "share-links",
    icon: Share2,
    title: "Read-only share links",
    body: "Share a signed, read-only view of your boat's history with anyone — and revoke it whenever you like.",
  },
];

export default function Features() {
  return (
    <section className="relative bg-[#0A192F] py-24 sm:py-32" data-testid="features-section">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(800px 400px at 50% 0%, rgba(29,138,153,0.1), transparent 65%)",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-[#25A5B7]">
            What Knot Forgotten does
          </p>
          <h2 className="mt-5 max-w-2xl text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            The ship's log, <span className="font-serif-display italic text-[#25A5B7]">reimagined.</span>
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
            Every feature below is part of the actual Knot Forgotten app — nothing aspirational,
            nothing on a roadmap.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal key={f.id} delay={(i % 4) * 0.07}>
                <article
                  data-testid={`feature-card-${f.id}`}
                  className="group h-full rounded-2xl border border-white/10 bg-[#0F213D] p-7 shadow-xl transition-all duration-300 hover:-translate-y-1.5 hover:border-[#1D8A99]/50 hover:bg-[#13294B]"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[#1D8A99]/30 bg-[#1D8A99]/10 text-[#25A5B7] transition-colors duration-300 group-hover:bg-[#1D8A99]/25">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 text-base font-semibold tracking-tight text-white">{f.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{f.body}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
